import { useCallback, useEffect, useState } from 'react';
import {
  addDoc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  runTransaction,
  setDoc,
  Timestamp,
  where,
} from 'firebase/firestore';
import { auth, db } from '../../../config/firebase';
import {
  publicTeamDirectoryDoc,
  teamDoc,
  teamInvitesCollection,
  teamRequestDoc,
  teamRequestsCollection,
  userDoc,
} from '../../../firebase/collections';
import { useAuth } from '../../../hooks/useAuth';
import {
  deriveAdminUids,
  deriveMemberUids,
  MAX_TEAM_MEMBERS,
  type Team,
  type TeamMember,
  type TeamRequest,
} from '../types';
import {
  profileFromUserData,
  readPublicPlayerByUid,
} from '../utils/memberProfile';

export interface PendingRequestWithUser extends TeamRequest {
  requesterName: string;
  requesterSlug?: string;
  requesterAvatarUrl?: string | null;
}

export function useTeamRequests() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const clearError = useCallback(() => setError(null), []);

  const sendRequest = useCallback(
    async (teamId: string, shareCollection = true): Promise<void> => {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error('Not signed in.');

      setLoading(true);
      setError(null);

      try {
        const uid = currentUser.uid;
        const now = Timestamp.now();
        const teamSnap = await getDoc(teamDoc(teamId));
        if (!teamSnap.exists()) throw new Error('Team not found.');
        const teamData = teamSnap.data() as Team;

        if (teamData.visibility !== 'public') {
          throw new Error('This team does not accept join requests.');
        }
        if (teamData.members.length >= MAX_TEAM_MEMBERS) throw new Error('Team is full.');
        if (teamData.members.some((m) => m.uid === uid)) {
          throw new Error('You are already a member.');
        }

        const userSnap = await getDoc(userDoc(uid));
        const userData = userSnap.exists() ? userSnap.data() : {};
        if (userData?.teamId) {
          throw new Error('You must leave your current team before requesting to join another.');
        }

        const existingRequests = await getDocs(
          query(
            teamRequestsCollection(teamId),
            where('requesterId', '==', uid),
            where('status', '==', 'pending')
          )
        );
        if (!existingRequests.empty) {
          throw new Error('You already have a pending request for this team.');
        }

        const existingInvites = await getDocs(
          query(
            teamInvitesCollection(teamId),
            where('inviteeId', '==', uid),
            where('status', '==', 'pending')
          )
        );
        if (!existingInvites.empty) {
          throw new Error('You already have a pending invite from this team. Check your invites.');
        }

        // Always denormalize from the requester's own user doc — admins cannot
        // read users/{uid}, and publicCollections may be missing for some accounts.
        const publicProfile = await readPublicPlayerByUid(uid);
        const profile = profileFromUserData(
          uid,
          userData as Record<string, unknown> | undefined,
          publicProfile,
          currentUser.displayName,
          currentUser.photoURL
        );

        await addDoc(teamRequestsCollection(teamId), {
          teamId,
          requesterId: uid,
          status: 'pending',
          shareCollection,
          createdAt: now,
          respondedAt: null,
          requesterName: profile.displayName,
          requesterSlug: profile.slug,
          requesterAvatarUrl: profile.avatarUrl,
        });
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Failed to send request.';
        setError(message);
        throw new Error(message);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const acceptRequest = useCallback(async (teamId: string, requestId: string): Promise<void> => {
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error('Not signed in.');

    setLoading(true);
    setError(null);

    try {
      const adminUid = currentUser.uid;
      const now = Timestamp.now();
      const tRef = teamDoc(teamId);
      const requestRef = teamRequestDoc(teamId, requestId);

      const teamSnap = await getDoc(tRef);
      if (!teamSnap.exists()) throw new Error('Team not found.');
      const teamData = teamSnap.data() as Team;
      const callerMember = teamData.members.find((m) => m.uid === adminUid);
      if (!callerMember || (callerMember.role !== 'owner' && callerMember.role !== 'admin')) {
        throw new Error('Only the team owner or admins can accept requests.');
      }

      const requestSnap = await getDoc(requestRef);
      if (!requestSnap.exists()) throw new Error('Request not found.');
      const requestData = requestSnap.data() as TeamRequest;
      if (requestData.status !== 'pending') throw new Error('Request is no longer pending.');
      const requesterUid = requestData.requesterId;
      const requestShare = requestData.shareCollection !== false;
      const userRef = userDoc(requesterUid);

      // Admins cannot read users/{requesterUid}. Prefer denormalized request fields,
      // then world-readable public profile. sendRequest already rejects applicants
      // who already have a teamId, so we do not leave-old-team here.
      const publicProfile = await readPublicPlayerByUid(requesterUid);
      const profile = profileFromUserData(
        requesterUid,
        {
          displayName: requestData.requesterName,
          slug: requestData.requesterSlug,
          avatarUrl: requestData.requesterAvatarUrl,
        },
        publicProfile
      );

      await runTransaction(db, async (transaction) => {
        const teamSnap2 = await transaction.get(tRef);
        if (!teamSnap2.exists()) throw new Error('Team not found.');
        const teamData2 = teamSnap2.data() as Team;

        if (teamData2.members.length >= MAX_TEAM_MEMBERS) throw new Error('Team is full.');
        if (teamData2.members.some((m) => m.uid === requesterUid)) {
          throw new Error('Player is already a member.');
        }

        const newMember: TeamMember = {
          uid: requesterUid,
          role: 'member',
          shareCollection: requestShare,
          joinedAt: now,
          ...profile,
        };
        const updatedMembers = [...teamData2.members, newMember];

        transaction.set(
          tRef,
          {
            members: updatedMembers,
            memberUids: deriveMemberUids(updatedMembers),
            adminUids: deriveAdminUids(updatedMembers),
            updatedAt: now,
          },
          { merge: true }
        );
        transaction.set(
          publicTeamDirectoryDoc(teamData2.slug),
          { memberCount: updatedMembers.length, updatedAt: now },
          { merge: true }
        );
        transaction.set(
          requestRef,
          { status: 'accepted', respondedAt: now, respondedBy: adminUid },
          { merge: true }
        );
        // Rules only allow admins to change teamId/updatedAt on another user's doc.
        // Collection visibility upgrade happens on the new member's next session
        // via useTeamCollectionHeal when shareCollection is true.
        transaction.set(userRef, { teamId, updatedAt: now }, { merge: true });
      });

      const pendingInvites = await getDocs(
        query(
          teamInvitesCollection(teamId),
          where('inviteeId', '==', requesterUid),
          where('status', '==', 'pending')
        )
      );
      for (const invite of pendingInvites.docs) {
        await setDoc(
          invite.ref,
          { status: 'cancelled', respondedAt: Timestamp.now() },
          { merge: true }
        );
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to accept request.';
      setError(message);
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  const refuseRequest = useCallback(async (teamId: string, requestId: string): Promise<void> => {
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error('Not signed in.');

    setLoading(true);
    setError(null);

    try {
      const teamSnap = await getDoc(teamDoc(teamId));
      if (!teamSnap.exists()) throw new Error('Team not found.');
      const teamData = teamSnap.data() as Team;
      const caller = teamData.members.find((m) => m.uid === currentUser.uid);
      if (!caller || (caller.role !== 'owner' && caller.role !== 'admin')) {
        throw new Error('Only the team owner or admins can refuse requests.');
      }
      await setDoc(
        teamRequestDoc(teamId, requestId),
        {
          status: 'refused',
          respondedAt: Timestamp.now(),
          respondedBy: currentUser.uid,
        },
        { merge: true }
      );
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to refuse request.';
      setError(message);
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  return { sendRequest, acceptRequest, refuseRequest, loading, error, clearError };
}

export function usePendingRequests(teamId: string | null | undefined) {
  const [requests, setRequests] = useState<PendingRequestWithUser[]>([]);
  const [loading, setLoading] = useState(Boolean(teamId));

  useEffect(() => {
    if (!teamId) {
      setRequests([]);
      setLoading(false);
      return;
    }

    const q = query(teamRequestsCollection(teamId), where('status', '==', 'pending'));
    const unsub = onSnapshot(
      q,
      async (snap) => {
        const rows = await Promise.all(
          snap.docs.map(async (d) => {
            const raw = d.data() as TeamRequest;
            const data = { ...raw, id: d.id };
            let requesterName =
              typeof raw.requesterName === 'string' && raw.requesterName.trim()
                ? raw.requesterName.trim()
                : '';
            let requesterSlug =
              typeof raw.requesterSlug === 'string' && raw.requesterSlug
                ? raw.requesterSlug
                : undefined;
            let requesterAvatarUrl =
              typeof raw.requesterAvatarUrl === 'string' ? raw.requesterAvatarUrl : null;

            // Backfill for older requests that never denormalized profile fields.
            if (!requesterName || !requesterSlug) {
              const profile = await readPublicPlayerByUid(data.requesterId);
              if (profile) {
                requesterName = requesterName || profile.displayName;
                requesterSlug = requesterSlug || profile.slug;
                requesterAvatarUrl = requesterAvatarUrl ?? profile.avatarUrl;
              }
            }

            return {
              ...data,
              shareCollection: data.shareCollection !== false,
              requesterName: requesterName || 'Player',
              requesterSlug,
              requesterAvatarUrl,
            };
          })
        );
        setRequests(rows);
        setLoading(false);
      },
      () => {
        setRequests([]);
        setLoading(false);
      }
    );
    return unsub;
  }, [teamId]);

  return { requests, loading };
}

export function useMyRequest(teamId: string | null | undefined) {
  const { user } = useAuth();
  const [request, setRequest] = useState<(TeamRequest & { id: string }) | null>(null);
  const [loading, setLoading] = useState(Boolean(teamId && user));

  useEffect(() => {
    if (!teamId || !user) {
      setRequest(null);
      setLoading(false);
      return;
    }

    const q = query(
      teamRequestsCollection(teamId),
      where('requesterId', '==', user.uid),
      where('status', '==', 'pending')
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const first = snap.docs[0];
        setRequest(first ? { ...(first.data() as TeamRequest), id: first.id } : null);
        setLoading(false);
      },
      () => {
        setRequest(null);
        setLoading(false);
      }
    );
    return unsub;
  }, [teamId, user]);

  // Older requests omitted denormalized profile fields; requester can patch their own doc.
  useEffect(() => {
    if (!teamId || !user || !request?.id) return;
    if (request.requesterName && request.requesterSlug) return;

    const requestId = request.id;
    void (async () => {
      try {
        const publicProfile = await readPublicPlayerByUid(user.uid);
        const profile = profileFromUserData(
          user.uid,
          {
            displayName: user.displayName,
            slug: user.slug,
            avatarUrl: user.avatarUrl,
            photoURL: user.photoURL,
          },
          publicProfile,
          user.displayName,
          user.photoURL
        );
        await setDoc(
          teamRequestDoc(teamId, requestId),
          {
            requesterName: profile.displayName,
            requesterSlug: profile.slug,
            requesterAvatarUrl: profile.avatarUrl,
          },
          { merge: true }
        );
      } catch {
        // Non-fatal — accept still works without the label.
      }
    })();
  }, [teamId, user, request?.id, request?.requesterName, request?.requesterSlug]);

  return { request, loading };
}
